import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  RefreshCw, AlertOctagon, CalendarClock, CheckCircle2, MessageCircle,
  Mail, Send, Phone, Search, Loader2, X, MoreVertical, Settings2, Plus, HeartPulse,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { AdherencePanel } from '../components/refills/AdherencePanel';

const TABS = [
  { value: 'due',       label: 'Due',       icon: CalendarClock, tone: 'amber',   countKey: 'due' },
  { value: 'overdue',   label: 'Overdue',   icon: AlertOctagon,  tone: 'rose',    countKey: 'overdue' },
  { value: 'upcoming',  label: 'Upcoming',  icon: RefreshCw,     tone: 'sky',     countKey: 'upcoming' },
  { value: 'completed', label: 'Completed', icon: CheckCircle2,  tone: 'emerald', countKey: 'completed' },
  { value: 'all',       label: 'All',       icon: RefreshCw,     tone: 'slate',   countKey: 'total' },
  { value: 'adherence', label: 'Adherence', icon: HeartPulse,    tone: 'violet',  countKey: 'falling_behind' },
];

const STATUS_TONE = {
  pending:   { label: 'Pending',   bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
  due:       { label: 'Due',       bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  overdue:   { label: 'Overdue',   bg: 'bg-rose-100',  text: 'text-rose-800',  dot: 'bg-rose-500'  },
  completed: { label: 'Completed', bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelled', bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-300' },
};

function relativeDays(n) {
  if (n == null) return '';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n === -1) return 'Yesterday';
  if (n > 0) return `In ${n} days`;
  return `${Math.abs(n)} days ago`;
}

export default function RefillsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [policyOpen, setPolicyOpen] = useState(false);
  const [tab, setTab] = useState('due');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ overdue: 0, due: 0, upcoming: 0, completed: 0, total: 0 });
  const [refills, setRefills] = useState([]);
  const [adherence, setAdherence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: list }, { data: s }, { data: adh }] = await Promise.all([
        api.get('/refills', { params: { status: tab === 'adherence' ? 'all' : tab, search, limit: 100 } }),
        api.get('/refills/stats'),
        api.get('/refills/adherence'),
      ]);
      setRefills(list?.refills || []);
      setAdherence(adh || null);
      setStats({ ...(s || stats), falling_behind: adh?.summary?.falling_behind || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load refills');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const send = async (refill, channels) => {
    setSending(refill.refill_id);
    try {
      const { data } = await api.post(`/refills/${refill.refill_id}/send`, { channels });
      const ok = (data?.results || []).filter(r => r.ok && r.channel !== 'whatsapp').length;
      const wa = (data?.results || []).find(r => r.channel === 'whatsapp');
      if (wa?.link && (channels?.includes('whatsapp') || !channels)) {
        window.open(wa.link, '_blank', 'noopener,noreferrer');
      }
      toast.success(ok > 0 ? `Reminder sent via ${ok} channel${ok > 1 ? 's' : ''}` : (wa ? 'WhatsApp link opened' : 'No channels reached the patient'));
      await fetchAll(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSending(null);
    }
  };

  const complete = async (refill) => {
    try {
      await api.put(`/refills/${refill.refill_id}/complete`);
      toast.success('Refill completed');
      await fetchAll(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to complete');
    }
  };

  const submitCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    try {
      await api.put(`/refills/${cancelTarget.refill_id}/cancel`, { reason: cancelReason.trim() });
      toast.success('Refill cancelled');
      setCancelTarget(null);
      setCancelReason('');
      await fetchAll(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    }
  };

  const runNow = async () => {
    try {
      const { data } = await api.post('/refills/run-now');
      toast.success(`Cron tick: sent=${data.sent || 0}, skipped=${data.skipped || 0}, failed=${data.failed || 0}`);
      await fetchAll(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run cron tick');
    }
  };

  const tabsWithCounts = useMemo(() => TABS.map(t => ({
    ...t, count: stats[t.countKey] || 0,
  })), [stats]);

  return (
    <div className="space-y-5" data-testid="refills-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>
            Refill Reminders
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-created on every dispensed sale. Nudges land in the patient&apos;s portal — plus Email, SMS and WhatsApp once configured — daily at 8am.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPolicyOpen(true)}
              className="gap-1.5"
              data-testid="refills-policy-btn"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Refill Policy
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={runNow} className="gap-1.5" data-testid="refills-run-now">
            <Send className="h-3.5 w-3.5" />
            Run cron now
          </Button>
          <Button variant="ghost" size="sm" onClick={() => fetchAll()} className="gap-1.5" disabled={loading} data-testid="refills-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tabsWithCounts.map(t => {
          const Icon = t.icon;
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              data-testid={`refill-stat-${t.value}`}
              className={[
                'rounded-xl border p-4 text-left transition-all',
                active ? `border-${t.tone}-300 bg-${t.tone}-50/70 ring-2 ring-${t.tone}-200`
                       : 'border-border bg-card hover:border-muted-foreground/30 hover:bg-muted/30',
              ].join(' ')}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`h-4 w-4 text-${t.tone}-600`} />
                <span className={`text-2xl font-bold tabular-nums text-${t.tone}-700`}>{t.count}</span>
              </div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t.label}</p>
            </button>
          );
        })}
      </div>

      {/* Search + tabs row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {tabsWithCounts.map(t => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`refill-tab-${t.value}`}>
                {t.label}
                <span className="ml-1.5 text-[10px] font-mono opacity-70">{t.count}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search patient or product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
            data-testid="refills-search"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : tab === 'adherence' ? (
        <AdherencePanel data={adherence} />
      ) : refills.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-muted py-16 text-center text-muted-foreground" data-testid="refills-empty">
          <RefreshCw className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No refills in this view yet.</p>
          <p className="text-xs opacity-70 mt-1">Reminders are auto-created when dispensable items are sold to a registered patient.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[11px] uppercase font-semibold tracking-wide text-muted-foreground border-b bg-muted/30">
            <div className="col-span-3">Patient</div>
            <div className="col-span-3">Medication</div>
            <div className="col-span-2">Due</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-center">Reminders</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {refills.map((r) => {
            const status = r.status_computed || r.status || 'pending';
            const tone = STATUS_TONE[status] || STATUS_TONE.pending;
            const dueDate = r.due_date ? new Date(r.due_date) : null;
            const dueStr = dueDate ? dueDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
            const isFinal = status === 'completed' || status === 'cancelled';
            return (
              <div
                key={r.refill_id}
                className="grid grid-cols-12 gap-3 px-4 py-3 border-b last:border-0 items-center hover:bg-muted/20 transition-colors"
                data-testid={`refill-row-${r.refill_id}`}
              >
                <div className="col-span-3 min-w-0">
                  <p className="text-sm font-medium truncate">{r.patient_name || 'Unnamed patient'}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    {r.patient_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{r.patient_phone}</span>}
                    {r.patient_email && <span className="truncate flex items-center gap-1"><Mail className="h-3 w-3" />{r.patient_email}</span>}
                  </p>
                </div>
                <div className="col-span-3 min-w-0">
                  <p className="text-sm truncate">{r.product_name || '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    Qty {r.quantity_dispensed} · {r.days_supply}-day supply
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm tabular-nums">{dueStr}</p>
                  <p className={`text-[11px] ${r.days_until_due < 0 ? 'text-rose-600 font-medium' : r.days_until_due <= 3 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                    {relativeDays(r.days_until_due)}
                  </p>
                </div>
                <div className="col-span-1">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                    {tone.label}
                  </span>
                </div>
                <div className="col-span-1 text-center text-xs text-muted-foreground tabular-nums">
                  {r.reminder_count || 0}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1.5">
                  {!isFinal && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => send(r)}
                        disabled={sending === r.refill_id}
                        className="h-8 gap-1 text-xs"
                        data-testid={`refill-send-${r.refill_id}`}
                      >
                        {sending === r.refill_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                        Remind
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => complete(r)}
                        className="h-8 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`refill-complete-${r.refill_id}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Done
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`refill-menu-${r.refill_id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => send(r, ['email'])} className="gap-2 text-xs">
                            <Mail className="h-3.5 w-3.5" /> Email only
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => send(r, ['sms'])} className="gap-2 text-xs">
                            <MessageCircle className="h-3.5 w-3.5" /> SMS only
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => send(r, ['whatsapp'])} className="gap-2 text-xs">
                            <Phone className="h-3.5 w-3.5" /> WhatsApp link
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setCancelTarget(r)} className="gap-2 text-xs text-rose-600">
                            <X className="h-3.5 w-3.5" /> Cancel refill
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                  {isFinal && (
                    <span className="text-[11px] text-muted-foreground">
                      {status === 'completed' ? 'Picked up' : `Cancelled${r.cancelled_reason ? ': ' + r.cancelled_reason : ''}`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel reason dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) { setCancelTarget(null); setCancelReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="refill-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel refill reminder</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Patient: <b>{cancelTarget?.patient_name}</b> · Medication: <b>{cancelTarget?.product_name}</b>
          </p>
          <Input
            placeholder="Reason (e.g. patient discontinued, allergy, doctor changed Rx)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            data-testid="refill-cancel-reason"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setCancelTarget(null); setCancelReason(''); }}>Back</Button>
            <Button onClick={submitCancel} disabled={!cancelReason.trim()} className="bg-rose-600 hover:bg-rose-700" data-testid="refill-cancel-confirm">
              Cancel refill
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Refill Policy panel */}
      {isAdmin && (
        <RefillPolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} />
      )}
    </div>
  );
}

function RefillPolicyDialog({ open, onOpenChange }) {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extraInput, setExtraInput] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/refills/policy')
      .then(({ data }) => setPolicy(data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load refill policy'))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const toggleBucket = (key) => {
    setPolicy(p => ({
      ...p,
      buckets: p.buckets.map(b => b.key === key ? { ...b, enabled: !b.enabled } : b),
    }));
  };

  const addExtraCategory = () => {
    const v = extraInput.trim().toLowerCase();
    if (!v) return;
    if ((policy.extra_refillable_categories || []).includes(v)) {
      setExtraInput('');
      return;
    }
    setPolicy(p => ({
      ...p,
      extra_refillable_categories: [...(p.extra_refillable_categories || []), v],
    }));
    setExtraInput('');
  };

  const removeExtraCategory = (v) => {
    setPolicy(p => ({
      ...p,
      extra_refillable_categories: (p.extra_refillable_categories || []).filter(x => x !== v),
    }));
  };

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const disabled = policy.buckets.filter(b => !b.enabled).map(b => b.key);
      const payload = {
        enabled: !!policy.enabled,
        disabled_chronic_buckets: disabled,
        extra_refillable_categories: policy.extra_refillable_categories || [],
        default_days_supply: Number(policy.default_days_supply) || 30,
        reminder_lead_days: Number(policy.reminder_lead_days) || 3,
        max_reminders: Number(policy.max_reminders) || 3,
        channels: policy.channels || ['email', 'sms', 'whatsapp'],
      };
      const { data } = await api.put('/refills/policy', payload);
      setPolicy(data);
      toast.success('Refill policy saved');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save refill policy');
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (ch) => {
    const cur = new Set(policy.channels || []);
    if (cur.has(ch)) cur.delete(ch); else cur.add(ch);
    setPolicy(p => ({ ...p, channels: Array.from(cur) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="refill-policy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-emerald-600" />
            Refill Policy
          </DialogTitle>
          <DialogDescription>
            Choose which medication categories auto-create refill reminders. Reminders are
            opt-in by category — only chronic-care drugs and items you explicitly add are tracked.
          </DialogDescription>
        </DialogHeader>

        {loading || !policy ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading policy…
          </div>
        ) : (
          <div className="space-y-5 pt-2">
            <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
              <div>
                <Label className="font-semibold">Enable refill reminders</Label>
                <p className="text-xs text-muted-foreground">Master switch for the entire refill pipeline.</p>
              </div>
              <Switch
                checked={!!policy.enabled}
                onCheckedChange={(v) => setPolicy(p => ({ ...p, enabled: v }))}
                data-testid="policy-master-switch"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Chronic-care categories</Label>
              <p className="text-xs text-muted-foreground">
                Default include-list. Disable any category you don't want to auto-track.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {policy.buckets.map(b => (
                  <div
                    key={b.key}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2.5 transition-colors ${
                      b.enabled ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/10' : 'border-border bg-card'
                    }`}
                  >
                    <div className="min-w-0 flex-1 mr-2">
                      <p className="text-sm font-medium truncate">{b.label}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {(b.keywords || []).slice(0, 4).join(', ')}
                      </p>
                    </div>
                    <Switch
                      checked={!!b.enabled}
                      onCheckedChange={() => toggleBucket(b.key)}
                      data-testid={`policy-bucket-${b.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Custom refillable categories</Label>
              <p className="text-xs text-muted-foreground">
                Add additional category names (e.g. "thyroid", "anticoagulant") matched against product fields.
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. thyroid"
                  value={extraInput}
                  onChange={(e) => setExtraInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtraCategory(); } }}
                  className="h-9"
                  data-testid="policy-extra-cat-input"
                />
                <Button type="button" variant="outline" size="sm" onClick={addExtraCategory} data-testid="policy-extra-cat-add">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
              {(policy.extra_refillable_categories || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {policy.extra_refillable_categories.map(c => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200"
                      data-testid={`policy-extra-cat-${c}`}
                    >
                      {c}
                      <button type="button" onClick={() => removeExtraCategory(c)} className="opacity-60 hover:opacity-100">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Default days supply</Label>
                <Input
                  type="number"
                  min={1}
                  value={policy.default_days_supply ?? 30}
                  onChange={(e) => setPolicy(p => ({ ...p, default_days_supply: e.target.value }))}
                  className="h-9"
                  data-testid="policy-days-supply"
                />
              </div>
              <div>
                <Label className="text-xs">Reminder lead (days)</Label>
                <Input
                  type="number"
                  min={0}
                  value={policy.reminder_lead_days ?? 3}
                  onChange={(e) => setPolicy(p => ({ ...p, reminder_lead_days: e.target.value }))}
                  className="h-9"
                  data-testid="policy-lead-days"
                />
              </div>
              <div>
                <Label className="text-xs">Max reminders / refill</Label>
                <Input
                  type="number"
                  min={1}
                  value={policy.max_reminders ?? 3}
                  onChange={(e) => setPolicy(p => ({ ...p, max_reminders: e.target.value }))}
                  className="h-9"
                  data-testid="policy-max-reminders"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Channels</Label>
              <div className="flex gap-2 flex-wrap items-center">
                <span
                  className="px-3 py-1.5 rounded-full text-xs font-medium border bg-emerald-100 text-emerald-800 border-emerald-300"
                  data-testid="policy-channel-portal"
                  title="Always on — needs no SMS or email account"
                >
                  PORTAL · always on
                </span>
                {['email', 'sms', 'whatsapp'].map(ch => {
                  const on = (policy.channels || []).includes(ch);
                  return (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => toggleChannel(ch)}
                      data-testid={`policy-channel-${ch}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        on ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                           : 'bg-muted text-muted-foreground border-border hover:bg-muted/70'
                      }`}
                    >
                      {ch.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Portal nudges appear in the patient&apos;s own portal with a one-tap refill
                request and need no credentials. Email, SMS and WhatsApp only fire once
                SMTP / Arkesel details are saved in Settings.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="policy-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="policy-save">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Save policy
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
